/**
 * Modal pour ajuster le stock d'un produit
 * Design System Volvo - Teal accent
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Package, Plus, Minus, RotateCcw, ArrowRightLeft, Undo2, Archive } from 'lucide-react';
import { productsAPI } from '../../../utils/productsAPI';
import type { Product } from '../../../types/models';

type StockMovementType = 'IN' | 'OUT' | 'ADJUSTMENT' | 'INITIAL' | 'RETURN' | 'TRANSFER';

interface StockAdjustModalProps {
  product: Product;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const movementTypes: { value: StockMovementType; labelKey: string; icon: React.ElementType; descKey: string }[] = [
  { value: 'IN', labelKey: 'products.stock.movementTypes.IN', icon: Plus, descKey: 'products.stock.movementTypes.IN_desc' },
  { value: 'OUT', labelKey: 'products.stock.movementTypes.OUT', icon: Minus, descKey: 'products.stock.movementTypes.OUT_desc' },
  { value: 'ADJUSTMENT', labelKey: 'products.stock.movementTypes.ADJUSTMENT', icon: RotateCcw, descKey: 'products.stock.movementTypes.ADJUSTMENT_desc' },
  { value: 'RETURN', labelKey: 'products.stock.movementTypes.RETURN', icon: Undo2, descKey: 'products.stock.movementTypes.RETURN_desc' },
  { value: 'TRANSFER', labelKey: 'products.stock.movementTypes.TRANSFER', icon: ArrowRightLeft, descKey: 'products.stock.movementTypes.TRANSFER_desc' },
  { value: 'INITIAL', labelKey: 'products.stock.movementTypes.INITIAL', icon: Archive, descKey: 'products.stock.movementTypes.INITIAL_desc' },
];

export const StockAdjustModal: React.FC<StockAdjustModalProps> = ({
  product,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [type, setType] = useState<StockMovementType>('IN');
  const [quantity, setQuantity] = useState<number>(1);
  const [reason, setReason] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const currentStock = product.stockQuantity !== null && product.stockQuantity !== undefined
    ? Number(product.stockQuantity)
    : 0;

  const calculateNewStock = () => {
    if (type === 'OUT') {
      return currentStock - quantity;
    }
    return currentStock + quantity;
  };

  const newStock = calculateNewStock();
  const isNegativeStock = type !== 'ADJUSTMENT' && newStock < 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (quantity <= 0) {
      setError(t('products.stock.quantityPositive'));
      return;
    }

    if (isNegativeStock) {
      setError(t('products.stock.cannotBeNegative'));
      return;
    }

    setLoading(true);
    try {
      await productsAPI.adjustStock(product.id, {
        type,
        quantity,
        reason: reason.trim() || undefined,
      });
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || t('products.stock.adjustError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-md"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl shadow-gray-400/20 w-full max-w-lg mx-4 overflow-hidden animate-fade-in-up border border-white/50">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/30 bg-gradient-to-r from-teal-50/80 to-white/60 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 rounded-xl">
              <Package className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">{t('products.stock.adjustTitle')}</h2>
              <p className="text-sm text-gray-500 truncate max-w-[250px]">{product.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Stock actuel */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
            <span className="text-sm font-medium text-gray-600">{t('products.stock.currentStock')}</span>
            <span className="text-2xl font-bold text-gray-900">
              {currentStock} <span className="text-sm font-normal text-gray-500">{product.unit || t('products.stock.units')}</span>
            </span>
          </div>

          {/* Type de mouvement */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {t('products.stock.movementType')}
            </label>
            <div className="grid grid-cols-3 gap-2">
              {movementTypes.map((mt) => {
                const Icon = mt.icon;
                const isSelected = type === mt.value;
                return (
                  <button
                    key={mt.value}
                    type="button"
                    onClick={() => setType(mt.value)}
                    className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all ${
                      isSelected
                        ? 'border-teal-500 bg-teal-50 text-teal-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-600'
                    }`}
                  >
                    <Icon className={`w-5 h-5 mb-1 ${isSelected ? 'text-teal-600' : 'text-gray-400'}`} />
                    <span className="text-xs font-medium">{t(mt.labelKey)}</span>
                  </button>
                );
              })}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              {t(movementTypes.find(m => m.value === type)?.descKey || '')}
            </p>
          </div>

          {/* Quantité */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('products.stock.quantity')}
            </label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="p-3 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                <Minus className="w-5 h-5 text-gray-600" />
              </button>
              <input
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                className="flex-1 px-4 py-3 text-center text-xl font-bold border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
              <button
                type="button"
                onClick={() => setQuantity(quantity + 1)}
                className="p-3 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                <Plus className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Aperçu nouveau stock */}
          <div className={`flex items-center justify-between p-4 rounded-xl ${
            isNegativeStock ? 'bg-red-50 border border-red-200' : 'bg-teal-50'
          }`}>
            <span className="text-sm font-medium text-gray-600">{t('products.stock.newStock')}</span>
            <span className={`text-2xl font-bold ${
              isNegativeStock ? 'text-red-600' : 'text-teal-700'
            }`}>
              {newStock} <span className="text-sm font-normal text-gray-500">{product.unit || t('products.stock.units')}</span>
            </span>
          </div>

          {/* Raison */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('products.stock.reasonOptional')}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('products.stock.reasonPlaceholder')}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
              rows={2}
              maxLength={500}
            />
          </div>

          {/* Erreur */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-colors"
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || isNegativeStock}
              className={`flex-1 px-4 py-3 text-white rounded-xl font-medium transition-all ${
                loading || isNegativeStock
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-teal-600 hover:bg-teal-700 hover:shadow-lg'
              }`}
            >
              {loading ? t('products.stock.saving') : t('common.actions.validate')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StockAdjustModal;
